import { findUnknownBlockingTags } from "@/lib/blocking-tags/unknown-blocking-tags";
import type { KnownTagsResponseDto } from "@babackapp/private-api-client/types";
import useGetShopKnownTags from "@babackapp/private-api-client/client/use-get-shop-known-tags";
import FormTagsInput from "@babackapp/react-hook-forms-components/form-tags-input";
import useTranslation from "@babackapp/translations/use-translation";
import { forwardRef, useMemo, type ComponentProps } from "react";
import { useParams } from "react-router-dom";

/**
 * What the list is compared against on the server:
 * - `order-or-product`: order tags, then product tags (refund, exchange,
 *   exchange credit, store credit);
 * - `order`: order tags only (refused orders);
 * - `product`: product tags only (non-returnable items).
 */
export type BlockingTagsMatch = "order-or-product" | "order" | "product";

type BlockingTagsInputProps = ComponentProps<typeof FormTagsInput> & {
  matches: BlockingTagsMatch;
};

/**
 * Tags input for the lists that block a resolution or an order, warning on a
 * value that matches no tag seen on the shop: the comparison is strict, so
 * such a value blocks nothing, silently (BBS-423).
 */
const BlockingTagsInput = forwardRef(
  ({ matches, value = [], ...props }: BlockingTagsInputProps, ref) => {
    const t = useTranslation();
    const { shopId = "" } = useParams<{ shopId: string }>();
    const { data } = useGetShopKnownTags({ shopId });

    const source = useMemo(() => getKnownTags(matches, data), [matches, data]);
    const unknownTags = useMemo(
      () =>
        source
          ? findUnknownBlockingTags(value, source.knownTags, {
              isComplete: source.isComplete,
            })
          : [],
      [value, source],
    );

    const tagWarnings = new Map(
      unknownTags.map(({ tag, suggestion }) => {
        const unseen = t("settings.blockingTags.unseenTag", { tag });
        return [
          tag,
          suggestion
            ? `${unseen} ${t("settings.blockingTags.didYouMean", { suggestion })}`
            : unseen,
        ];
      }),
    );

    return (
      <FormTagsInput
        {...props}
        ref={ref}
        value={value}
        tagWarnings={tagWarnings}
      />
    );
  },
);

BlockingTagsInput.displayName = "BlockingTagsInput";

/**
 * The tags to check against, and whether they cover every tag in use. Order
 * tags are complete only with tracked orders: returns never include the
 * orders a tag kept from getting one. Product tags are complete only with a
 * synced catalog, for the same reason with excluded products.
 */
function getKnownTags(
  matches: BlockingTagsMatch,
  data: KnownTagsResponseDto | undefined,
): { knownTags: ReadonlySet<string>; isComplete: boolean } | undefined {
  if (!data) return undefined;

  switch (matches) {
    case "order":
      return {
        knownTags: new Set(data.orderTags),
        isComplete: data.hasTrackedOrders,
      };
    case "product":
      return {
        knownTags: new Set(data.productTags),
        isComplete: data.hasCatalog,
      };
    case "order-or-product":
      return {
        knownTags: new Set([...data.orderTags, ...data.productTags]),
        isComplete: data.hasTrackedOrders && data.hasCatalog,
      };
  }
}

export default BlockingTagsInput;
